import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDownLeft, ArrowLeft, ArrowUpRight, Minus, Paperclip, Pencil, PieChart, Plus, Undo2, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated, type UploadedFile } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
  buttonClass,
} from '../components/ui';

/**
 * Partners (Finance Phase 1, 2026-10-28): what each owner owns, and their money
 * with the company — four balances kept apart, never netted, each per currency.
 */

export interface PartnerPosition {
  currency: string;
  loanPayable: string;
  advanceReceivable: string;
  equityContributed: string;
  distributions: string;
  loanRepaid: string;
  informationalNet: string;
}

export interface PartnerRef {
  id: string;
  name: string;
  nameAr: string | null;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface PartnerTx {
  id: string;
  number: string;
  partner: PartnerRef;
  type: TxType;
  transactionDate: string;
  amount: string;
  currency: string;
  exchangeRate: string;
  baseCurrency: string;
  baseAmount: string;
  moneyAccount: { id: string; name: string; nameAr: string | null; type: string; currency: string } | null;
  expense: { id: string; expenseDate: string; description: string; amount: string; currency: string; status: string } | null;
  fundingType: 'LOAN' | 'CAPITAL' | null;
  adjustedBalance: 'LOAN_PAYABLE' | 'ADVANCE_RECEIVABLE' | 'EQUITY' | null;
  adjustmentDirection: 'INCREASE' | 'DECREASE' | null;
  reference: string | null;
  notes: string | null;
  attachment: UploadedFile | null;
  postingStatus: 'POSTED' | 'REVERSED';
  counted: boolean;
  cashDirection: 1 | -1 | 0;
  createdAt: string;
  reversedAt: string | null;
  reversalReason: string | null;
  balancesAfter?: PartnerPosition | null;
  beforeCutover?: boolean;
}

export type TxType =
  | 'CAPITAL_CONTRIBUTION'
  | 'LOAN_RECEIVED'
  | 'LOAN_REPAYMENT'
  | 'ADVANCE_PAID'
  | 'ADVANCE_REPAYMENT'
  | 'PROFIT_DISTRIBUTION'
  | 'PAID_COMPANY_EXPENSE'
  | 'ADJUSTMENT';

/** The types, as the form groups them: what moves money in, what moves it out, what moves none. */
export const TX_GROUPS: Array<{ label: TranslationKey; types: TxType[] }> = [
  { label: 'ptx.groupIn', types: ['LOAN_RECEIVED', 'CAPITAL_CONTRIBUTION', 'ADVANCE_REPAYMENT'] },
  { label: 'ptx.groupOut', types: ['LOAN_REPAYMENT', 'ADVANCE_PAID', 'PROFIT_DISTRIBUTION'] },
  { label: 'ptx.groupNone', types: ['PAID_COMPANY_EXPENSE', 'ADJUSTMENT'] },
];

export const TX_TYPES: TxType[] = TX_GROUPS.flatMap((g) => g.types);

/** "50.000000" → "50%", "33.333333" → "33.333333%". */
export function formatShare(value: string | number | null | undefined, locale: 'en' | 'ar'): string {
  if (value === null || value === undefined) return '—';
  const n = new Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en', { maximumFractionDigits: 6 }).format(Number(value));
  return `${n}%`;
}

/** Each currency on a line of its own — never added together. */
function PerCurrency({ positions, pick }: { positions: PartnerPosition[]; pick: (p: PartnerPosition) => string }) {
  const { locale } = useI18n();
  const shown = positions.filter((p) => Number(pick(p)) !== 0);
  if (shown.length === 0) return <span className="text-faint">—</span>;
  return (
    <span className="flex flex-col items-end gap-0.5">
      {shown.map((p) => (
        <span key={p.currency} className="tabular">{formatMoney(pick(p), p.currency, locale)}</span>
      ))}
    </span>
  );
}

/** Which way company money went, in words — no unexplained negative numbers. */
export function Direction({ value }: { value: 1 | -1 | 0 }) {
  const { t } = useI18n();
  if (value === 1)
    return (
      <span className="inline-flex items-center gap-1 text-success-fg dark:text-success">
        <ArrowDownLeft className="h-3.5 w-3.5" aria-hidden />
        {t('ptx.moneyIn')}
      </span>
    );
  if (value === -1)
    return (
      <span className="inline-flex items-center gap-1 text-warning-fg dark:text-warning-dark">
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        {t('ptx.moneyOut')}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-muted">
      <Minus className="h-3.5 w-3.5" aria-hidden />
      {t('ptx.noMoney')}
    </span>
  );
}

function CutoverNote({ cutoverDate }: { cutoverDate: string | null }) {
  const { t, locale } = useI18n();
  return (
    <p className="mb-4 text-body-sm text-muted">
      {cutoverDate ? t('partners.sinceCutover', { date: formatDate(cutoverDate, locale) }) : t('partners.noCutover')}
    </p>
  );
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

interface PartnersList {
  today: string;
  cutoverDate: string | null;
  ownership: { total: string; complete: boolean };
  items: Array<PartnerRef & { share: string | null; email: string | null; phone: string | null; positions: PartnerPosition[] }>;
}

/** Route: `/partners`. */
export function PartnersPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['partners', status, q],
    queryFn: () => api.get<PartnersList>('/partners', { status, q }),
  });

  return (
    <>
      <PageHeader
        title={t('partners.title')}
        subtitle={t('partners.subtitle')}
        count={data?.items.length}
        action={
          <>
            <Link to="/partner-transactions" className={buttonClass('secondary', 'md')}>
              {t('partners.allTransactions')}
            </Link>
            {can(P.PARTNERS_MANAGE) && (
              <>
                <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/partners/new')}>
                  {t('partners.new')}
                </Button>
                <Button variant="primary" onClick={() => navigate('/partner-transactions/new')}>
                  {t('partners.recordTransaction')}
                </Button>
              </>
            )}
          </>
        }
      />

      {data && (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Metric
            label={t('partners.ownershipTotal')}
            value={formatShare(data.ownership.total, locale)}
            tone={data.ownership.complete ? 'success' : 'warning'}
            hint={data.ownership.complete ? t('partners.ownershipComplete') : t('partners.ownershipOff')}
            icon={<PieChart className="h-4 w-4" />}
          />
        </div>
      )}
      {data && <CutoverNote cutoverDate={data.cutoverDate} />}

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          <Input placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('partners.status')} — {t('common.all')}</option>
            <option value="ACTIVE">{t('partners.active')}</option>
            <option value="INACTIVE">{t('partners.inactive')}</option>
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Users className="h-8 w-8" />} title={t('partners.empty')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('partners.name')}</Th>
                <Th align="end">{t('partners.shareToday')}</Th>
                <Th align="end">{t('partners.loanPayable')}</Th>
                <Th align="end">{t('partners.advanceReceivable')}</Th>
                <Th align="end">{t('partners.informationalNet')}</Th>
                <Th align="end">{t('partners.equity')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <Tr key={p.id} onClick={() => navigate(`/partners/${p.id}`)}>
                  <Td>
                    <Link to={`/partners/${p.id}`} className="font-medium text-[var(--text-primary)] hover:text-primary" onClick={(e) => e.stopPropagation()}>
                      {localized(p)}
                    </Link>
                    {p.status === 'INACTIVE' && <Badge tone="neutral" className="ms-2">{t('partners.inactive')}</Badge>}
                  </Td>
                  <Td align="end" className="tabular">{formatShare(p.share, locale)}</Td>
                  <Td align="end"><PerCurrency positions={p.positions} pick={(x) => x.loanPayable} /></Td>
                  <Td align="end"><PerCurrency positions={p.positions} pick={(x) => x.advanceReceivable} /></Td>
                  <Td align="end" className="text-muted"><PerCurrency positions={p.positions} pick={(x) => x.informationalNet} /></Td>
                  <Td align="end"><PerCurrency positions={p.positions} pick={(x) => x.equityContributed} /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// One partner
// ---------------------------------------------------------------------------

interface PartnerDetail extends PartnerRef {
  email: string | null;
  phone: string | null;
  notes: string | null;
  share: string | null;
  today: string;
  cutover: { cutoverDate: string; status: 'DRAFT' | 'CONFIRMED' } | null;
  shares: Array<{ id: string; percentage: string; effectiveFrom: string; effectiveTo: string | null; note: string | null }>;
  opening: Array<{ id: string; kind: string; currency: string; amount: string; notes: string | null }>;
  positions: PartnerPosition[];
  statement: PartnerTx[];
}

/** Route: `/partners/:id`. */
export function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['partners', id],
    queryFn: () => api.get<PartnerDetail>(`/partners/${id}`),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const confirmedCutover = data.cutover?.status === 'CONFIRMED' ? data.cutover.cutoverDate : null;
  const statement = [...data.statement].reverse();

  return (
    <>
      <div className="mb-4">
        <Link to="/partners" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('partners.title')}
        </Link>
      </div>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {localized(data)}
            <Badge tone={data.status === 'ACTIVE' ? 'success' : 'neutral'}>
              {data.status === 'ACTIVE' ? t('partners.active') : t('partners.inactive')}
            </Badge>
          </span>
        }
        subtitle={`${t('partners.shareToday')}: ${formatShare(data.share, locale)}`}
        action={
          can(P.PARTNERS_MANAGE) && (
            <>
              <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/partners/${data.id}/edit`)}>
                {t('common.edit')}
              </Button>
              <Button icon={<PieChart className="h-4 w-4" />} onClick={() => navigate(`/partners/${data.id}/shares/new`)}>
                {t('partners.changeShare')}
              </Button>
              <Button variant="primary" onClick={() => navigate(`/partner-transactions/new?partnerId=${data.id}`)}>
                {t('partners.recordTransaction')}
              </Button>
            </>
          )
        }
      />
      <CutoverNote cutoverDate={confirmedCutover} />

      {data.positions.length === 0 ? (
        <Card className="mb-5">
          <p className="text-body-sm text-muted">{t('partners.noMoney')}</p>
        </Card>
      ) : (
        data.positions.map((p) => (
          <section key={p.currency} className="mb-5">
            <h2 className="mb-2 text-overline uppercase text-[var(--text-tertiary)]">{t('partners.currencyBalances', { currency: p.currency })}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Metric label={t('partners.loanPayable')} value={formatMoney(p.loanPayable, p.currency, locale)} hint={t('partners.loanPayableHint')} />
              <Metric label={t('partners.advanceReceivable')} value={formatMoney(p.advanceReceivable, p.currency, locale)} hint={t('partners.advanceReceivableHint')} />
              <Metric label={t('partners.informationalNet')} value={formatMoney(p.informationalNet, p.currency, locale)} hint={t('partners.informationalNetHint')} />
              <Metric label={t('partners.equity')} value={formatMoney(p.equityContributed, p.currency, locale)} hint={t('partners.equityHint')} />
              <Metric label={t('partners.distributions')} value={formatMoney(p.distributions, p.currency, locale)} />
              <Metric label={t('partners.loanRepaid')} value={formatMoney(p.loanRepaid, p.currency, locale)} />
            </div>
          </section>
        ))
      )}

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('partners.shareHistory')} />
          <Table>
            <thead>
              <tr>
                <Th>{t('partners.share')}</Th>
                <Th>{t('partners.shareFrom')}</Th>
                <Th>{t('partners.shareTo')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.shares.map((s) => (
                <Tr key={s.id}>
                  <Td className="tabular">
                    {formatShare(s.percentage, locale)}
                    {s.note && <span className="block text-caption text-faint">{s.note}</span>}
                  </Td>
                  <Td className="tabular">{formatDate(s.effectiveFrom, locale)}</Td>
                  <Td className="tabular">{s.effectiveTo ? formatDate(s.effectiveTo, locale) : '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card>
          <CardHeader title={confirmedCutover ? t('partners.opening', { date: formatDate(confirmedCutover, locale) }) : t('partners.contact')} />
          {confirmedCutover ? (
            data.opening.length === 0 ? (
              <p className="text-body-sm text-muted">—</p>
            ) : (
              <dl>
                {data.opening.map((l) => (
                  <Detail key={l.id} label={t(`setup.kind.${l.kind}` as TranslationKey)}>
                    <span className="tabular">{formatMoney(l.amount, l.currency, locale)}</span>
                  </Detail>
                ))}
              </dl>
            )
          ) : (
            <dl>
              <Detail label={t('common.email')}>{data.email ?? '—'}</Detail>
              <Detail label={t('common.phone')}>{data.phone ?? '—'}</Detail>
            </dl>
          )}
          {data.notes && <p className="mt-3 whitespace-pre-line text-body-sm text-muted" dir="auto">{data.notes}</p>}
        </Card>
      </div>

      <Card padded={false}>
        <CardHeader title={t('partners.statement')} className="p-5 pb-0" />
        {statement.length === 0 ? (
          <EmptyState title={t('partners.noMoney')} />
        ) : (
          <TransactionTable rows={statement} showPartner={false} showBalances />
        )}
      </Card>
    </>
  );
}

/** Transactions, newest first; with `showBalances`, what each left a partner with. */
function TransactionTable({ rows, showPartner, showBalances = false }: { rows: PartnerTx[]; showPartner: boolean; showBalances?: boolean }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('ptx.date')}</Th>
          <Th>{t('ptx.number')}</Th>
          {showPartner && <Th>{t('ptx.partner')}</Th>}
          <Th>{t('ptx.type')}</Th>
          <Th>{t('ptx.account')}</Th>
          <Th align="end">{t('ptx.amount')}</Th>
          {showBalances && <Th align="end">{t('partners.balancesAfter')}</Th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((tx) => (
          <Tr key={tx.id} onClick={() => navigate(`/partner-transactions/${tx.id}`)} className={clsx(!tx.counted && 'opacity-70')}>
            <Td className="tabular whitespace-nowrap">{formatDate(tx.transactionDate, locale)}</Td>
            <Td className="font-mono text-mono-sm text-primary-600 dark:text-primary-400 whitespace-nowrap">
              {tx.number}
              {tx.attachment && <Paperclip className="ms-1 inline h-3.5 w-3.5 text-[var(--text-tertiary)]" aria-label={t('ptx.evidence')} />}
            </Td>
            {showPartner && <Td>{localized(tx.partner)}</Td>}
            <Td>
              <span className="block">{t(`ptx.${tx.type}` as TranslationKey)}</span>
              <span className="mt-0.5 flex flex-wrap gap-1.5">
                {tx.postingStatus === 'REVERSED' && <Badge tone="error">{t('partners.reversed')}</Badge>}
                {tx.beforeCutover && tx.postingStatus !== 'REVERSED' && <Badge tone="neutral">{t('partners.history')}</Badge>}
              </span>
            </Td>
            <Td className="text-body-sm">
              {tx.moneyAccount ? localized(tx.moneyAccount) : tx.expense ? <span className="text-muted" dir="auto">{tx.expense.description}</span> : '—'}
              <span className="block text-caption"><Direction value={tx.cashDirection} /></span>
            </Td>
            <Td align="end" className="tabular whitespace-nowrap">{formatMoney(tx.amount, tx.currency, locale)}</Td>
            {showBalances && (
              <Td align="end" className="tabular text-caption text-muted whitespace-nowrap">
                {tx.balancesAfter ? (
                  <>
                    <span className="block">{t('partners.loanPayable')}: {formatMoney(tx.balancesAfter.loanPayable, tx.currency, locale)}</span>
                    <span className="block">{t('partners.advanceReceivable')}: {formatMoney(tx.balancesAfter.advanceReceivable, tx.currency, locale)}</span>
                  </>
                ) : (
                  '—'
                )}
              </Td>
            )}
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// All transactions
// ---------------------------------------------------------------------------

/** Route: `/partner-transactions`. */
export function PartnerTransactionsPage() {
  const { t, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const set = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['partner-transactions', page, filters],
    queryFn: () => api.get<Paginated<PartnerTx> & { cutoverDate: string | null }>('/partner-transactions', { page, pageSize: 25, ...filters }),
  });
  const partners = useQuery({ queryKey: ['partners', '', ''], queryFn: () => api.get<PartnersList>('/partners') }).data;
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Array<{ code: string }>>('/currencies') }).data;

  return (
    <>
      <PageHeader
        title={t('ptx.title')}
        count={data?.total}
        action={
          can(P.PARTNERS_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/partner-transactions/new')}>
              {t('partners.recordTransaction')}
            </Button>
          )
        }
      />
      {data && <CutoverNote cutoverDate={data.cutoverDate} />}
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <Input placeholder={t('common.search')} value={filters.q ?? ''} onChange={(e) => set('q', e.target.value)} />
          <Select value={filters.partnerId ?? ''} onChange={(e) => set('partnerId', e.target.value)}>
            <option value="">{t('ptx.partner')} — {t('common.all')}</option>
            {partners?.items.map((p) => (
              <option key={p.id} value={p.id}>{localized(p)}</option>
            ))}
          </Select>
          <Select value={filters.type ?? ''} onChange={(e) => set('type', e.target.value)}>
            <option value="">{t('ptx.type')} — {t('common.all')}</option>
            {TX_TYPES.map((type) => (
              <option key={type} value={type}>{t(`ptx.${type}` as TranslationKey)}</option>
            ))}
          </Select>
          <Select value={filters.currency ?? ''} onChange={(e) => set('currency', e.target.value)}>
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {currencies?.map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
          <Input type="date" aria-label={t('common.from')} value={filters.from ?? ''} onChange={(e) => set('from', e.target.value)} />
          <Input type="date" aria-label={t('common.to')} value={filters.to ?? ''} onChange={(e) => set('to', e.target.value)} />
        </div>
      </Card>
      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState />
        ) : (
          <>
            <TransactionTable rows={data.items} showPartner />
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// One transaction
// ---------------------------------------------------------------------------

/** Route: `/partner-transactions/:id`. */
export function PartnerTransactionPage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['partner-transactions', id],
    queryFn: () =>
      api.get<PartnerTx & { cutoverDate: string | null; journal: Array<{ account: { kind: string }; debit: string; credit: string }> }>(
        `/partner-transactions/${id}`,
      ),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const row = (label: TranslationKey, value: ReactNode) => <Detail label={t(label)}>{value}</Detail>;

  return (
    <>
      <div className="mb-4">
        <Link to={`/partners/${data.partner.id}`} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {localized(data.partner)}
        </Link>
      </div>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span className="font-mono">{data.number}</span>
            <Badge tone={data.postingStatus === 'REVERSED' ? 'error' : 'success'}>
              {data.postingStatus === 'REVERSED' ? t('partners.reversed') : t('ptx.posted')}
            </Badge>
          </span>
        }
        subtitle={t(`ptx.${data.type}` as TranslationKey)}
        action={
          can(P.PARTNERS_REVERSE) &&
          data.postingStatus === 'POSTED' && (
            <Button variant="danger" icon={<Undo2 className="h-4 w-4" />} onClick={() => navigate(`/partner-transactions/${data.id}/reverse`)}>
              {t('ptx.reverse')}
            </Button>
          )
        }
      />
      {data.reversedAt && (
        <Card className="mb-5 border-error/40">
          <p className="text-body-sm" dir="auto">{t('ptx.reversedOn', { date: formatDate(data.reversedAt, locale), reason: data.reversalReason ?? '' })}</p>
        </Card>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <dl>
            {row('ptx.partner', <Link to={`/partners/${data.partner.id}`} className="hover:text-primary">{localized(data.partner)}</Link>)}
            {row('ptx.date', <span className="tabular">{formatDate(data.transactionDate, locale)}</span>)}
            {row('ptx.amount', <span className="tabular">{formatMoney(data.amount, data.currency, locale)}</span>)}
            {data.currency !== data.baseCurrency &&
              row('payments.exchangeRate', <span className="tabular">1 {data.currency} = {data.exchangeRate} {data.baseCurrency}</span>)}
            {data.currency !== data.baseCurrency &&
              row('common.total', <span className="tabular">{formatMoney(data.baseAmount, data.baseCurrency, locale)}</span>)}
            {row('ptx.direction', <Direction value={data.cashDirection} />)}
            {data.moneyAccount && row('ptx.account', localized(data.moneyAccount))}
            {data.expense &&
              row(
                'ptx.expense',
                <span dir="auto">
                  {data.expense.description} · {formatMoney(data.expense.amount, data.expense.currency, locale)}
                </span>,
              )}
            {data.fundingType && row('ptx.funding', t(`ptx.funding${data.fundingType}` as TranslationKey))}
            {data.adjustedBalance && row('ptx.balance', t(`ptx.adjusted${data.adjustedBalance}` as TranslationKey))}
            {data.adjustmentDirection && row('ptx.direction', t(`ptx.${data.adjustmentDirection}` as TranslationKey))}
            {data.reference && row('ptx.reference', <span dir="auto">{data.reference}</span>)}
            {row('ptx.status', data.counted ? t('ptx.counted') : t('ptx.notCounted'))}
          </dl>
          {data.notes && <p className="mt-3 whitespace-pre-line text-body-sm text-muted" dir="auto">{data.notes}</p>}
          {data.attachment && (
            <a href={data.attachment.url} target="_blank" rel="noreferrer" className={clsx(buttonClass('secondary', 'sm'), 'mt-4 gap-1.5')}>
              <Paperclip className="h-4 w-4" aria-hidden />
              {data.attachment.originalName}
            </a>
          )}
        </Card>
        <Card>
          <CardHeader title={t('ptx.journal')} subtitle={t('ptx.journalHint')} />
          <Table>
            <thead>
              <tr>
                <Th>{t('ptx.account')}</Th>
                <Th align="end">{t('ptx.debit')}</Th>
                <Th align="end">{t('ptx.credit')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.journal.map((line, i) => (
                <Tr key={i}>
                  <Td>{t(`ptx.account.${line.account.kind}` as TranslationKey)}</Td>
                  <Td align="end" className="tabular">{Number(line.debit) ? formatMoney(line.debit, data.currency, locale) : ''}</Td>
                  <Td align="end" className="tabular">{Number(line.credit) ? formatMoney(line.credit, data.currency, locale) : ''}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>
    </>
  );
}
